import type { GridCell, GridCoordinate, MapDefinition } from "../domain/types";

export const coordinateKey = ({ x, y }: GridCoordinate): string => `${x},${y}`;

/** Owns terrain and occupancy. Rendering never determines navigation state. */
export class GridManager {
  readonly cells: GridCell[];
  private readonly towerCells = new Map<string, GridCoordinate[]>();

  constructor(readonly map: MapDefinition) {
    const entrances = new Set(map.entrances.map((entry) => coordinateKey(entry.cell)));
    const exits = new Set(map.exits.map((entry) => coordinateKey(entry.cell)));
    const protectedRoute = new Set(map.protectedCells.map(coordinateKey));
    const obstacles = new Map<string, string>();
    for (const obstacle of map.obstacles)
      for (const cell of obstacle.cells) obstacles.set(coordinateKey(cell), obstacle.id);
    this.cells = Array.from({ length: map.width * map.height }, (_, index) => {
      const x = index % map.width,
        y = Math.floor(index / map.width),
        key = `${x},${y}`;
      const terrain = map.terrain[key] ?? "grass",
        obstacleId = obstacles.get(key) ?? null;
      const isEntrance = entrances.has(key),
        isExit = exits.has(key);
      return {
        x,
        y,
        terrain,
        walkable: terrain !== "water" && obstacleId === null,
        buildable:
          terrain !== "water" &&
          obstacleId === null &&
          !isEntrance &&
          !isExit &&
          !protectedRoute.has(key),
        movementCost: map.movementCosts[terrain] ?? 1,
        towerId: null,
        obstacleId,
        isEntrance,
        isExit,
        protectedRoute: protectedRoute.has(key),
      };
    });
  }

  index({ x, y }: GridCoordinate): number {
    return y * this.map.width + x;
  }
  inside({ x, y }: GridCoordinate): boolean {
    return x >= 0 && y >= 0 && x < this.map.width && y < this.map.height;
  }
  cell(coordinate: GridCoordinate): GridCell | undefined {
    return this.inside(coordinate) ? this.cells[this.index(coordinate)] : undefined;
  }
  isWalkable(
    coordinate: GridCoordinate,
    additionallyBlocked: ReadonlySet<string> = new Set(),
  ): boolean {
    const cell = this.cell(coordinate);
    return Boolean(
      cell?.walkable &&
        cell.towerId === null &&
        !additionallyBlocked.has(coordinateKey(coordinate)),
    );
  }
  occupiedCoordinates(
    origin: GridCoordinate,
    footprint: readonly GridCoordinate[],
  ): GridCoordinate[] {
    return footprint.map((offset) => ({ x: origin.x + offset.x, y: origin.y + offset.y }));
  }

  basicPlacementCheck(
    origin: GridCoordinate,
    footprint: readonly GridCoordinate[],
  ): { valid: boolean; reason: string; cells: GridCoordinate[] } {
    const cells = this.occupiedCoordinates(origin, footprint);
    for (const coordinate of cells) {
      const cell = this.cell(coordinate);
      if (!cell) return { valid: false, reason: "Outside the buildable battlefield.", cells };
      if (cell.isEntrance || cell.isExit || cell.protectedRoute)
        return { valid: false, reason: "The marked royal road must remain clear.", cells };
      if (!cell.buildable)
        return { valid: false, reason: "That terrain cannot support a tower.", cells };
      if (cell.towerId || cell.obstacleId)
        return { valid: false, reason: "Another structure already occupies that space.", cells };
    }
    return { valid: true, reason: "Valid placement", cells };
  }

  occupy(towerId: string, cells: readonly GridCoordinate[]): void {
    if (this.towerCells.has(towerId)) throw new Error(`Tower ${towerId} already occupies the grid`);
    for (const coordinate of cells) {
      const cell = this.cell(coordinate);
      if (!cell || cell.towerId) throw new Error("Cannot occupy an unavailable grid cell");
      cell.towerId = towerId;
    }
    this.towerCells.set(
      towerId,
      cells.map((cell) => ({ ...cell })),
    );
  }

  release(towerId: string): readonly GridCoordinate[] {
    const cells = this.towerCells.get(towerId) ?? [];
    for (const coordinate of cells) {
      const cell = this.cell(coordinate);
      if (cell?.towerId === towerId) cell.towerId = null;
    }
    this.towerCells.delete(towerId);
    return cells;
  }

  coordinatesForTower(towerId: string): readonly GridCoordinate[] {
    return this.towerCells.get(towerId) ?? [];
  }
}
